/**
 * Format a date string for display without timezone conversion
 * This treats the date as a date-only value, ignoring time and timezone
 *
 * @param dateString - ISO date string from API
 * @returns Formatted date string (M/D/YYYY)
 */
export const formatDateOnly = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return 'N/A';

  // Fix the API's malformed timezone format: remove dot before timezone
  const fixedDate = dateString.replace(/\.(-|\+)/, '$1');

  // Parse the date string and extract just the date components
  // This avoids timezone conversion issues
  const date = new Date(fixedDate);

  // Get UTC date components to avoid timezone shifts
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Format a date string with timezone consideration
 * Use this when you actually want local timezone conversion
 *
 * @param dateString - ISO date string from API
 * @returns Formatted date string in local time
 */
export const formatDateTime = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return 'N/A';

  const fixedDate = dateString.replace(/\.(-|\+)/, '$1');
  return new Date(fixedDate).toLocaleDateString();
};

/**
 * Check if a date is in the past (for due date checking)
 *
 * @param dateString - ISO date string from API
 * @returns true if the date is before today
 */
export const isOverdue = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;

  const fixedDate = dateString.replace(/\.(-|\+)/, '$1');
  const dueDate = new Date(fixedDate);
  const today = new Date();

  // Compare at UTC midnight to avoid timezone issues
  const dueDateUTC = new Date(
    Date.UTC(
      dueDate.getUTCFullYear(),
      dueDate.getUTCMonth(),
      dueDate.getUTCDate()
    )
  );
  const todayUTC = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  );

  return dueDateUTC < todayUTC;
};
